import { useState, useEffect } from 'react';
import { Question, QuestionOption, TalentType } from './TalentTestData';
import { useLang } from '../../../i18n/LanguageContext';

interface TalentTestQuestionProps {
  question: Question;
  questionIndex: number;
  totalQuestions: number;
  onAnswer: (type: TalentType) => void;
  onBack: () => void;
}

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

const OPTION_COLORS = [
  { border: '#a78bfa', bg: 'rgba(167,139,250,0.12)', label: '#a78bfa' },
  { border: '#f4d03f', bg: 'rgba(244,208,63,0.12)', label: '#f4d03f' },
  { border: '#34d399', bg: 'rgba(52,211,153,0.12)', label: '#34d399' },
  { border: '#f9a8d4', bg: 'rgba(249,168,212,0.12)', label: '#f9a8d4' },
];

export default function TalentTestQuestion({
  question,
  questionIndex,
  totalQuestions,
  onAnswer,
  onBack,
}: TalentTestQuestionProps) {
  const { t } = useLang();
  const [selected, setSelected] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    setSelected(null);
    setRevealed(false);
    const t = setTimeout(() => setRevealed(true), 60);
    return () => clearTimeout(t);
  }, [question.id]);

  const handleSelect = (idx: number, option: QuestionOption) => {
    if (selected !== null || animating) return;
    setSelected(idx);
    setAnimating(true);
    setTimeout(() => {
      onAnswer(option.type);
      setAnimating(false);
    }, 650);
  };

  const progress = (questionIndex / totalQuestions) * 100;

  return (
    <div className={`transition-all duration-500 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

      {/* 顶部进度 */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[#c8b6ff]/50 text-xs tracking-widest uppercase">
            {questionIndex + 1} / {totalQuestions}
          </span>
          <span className="text-xs font-medium" style={{ color: '#d4af37' }}>
            {Math.round(progress)}% {t('Done', '已完成')}
          </span>
        </div>
        {/* 分段进度条 */}
        <div className="flex gap-1">
          {Array.from({ length: totalQuestions }).map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1 rounded-full transition-all duration-500"
              style={{
                background:
                  i < questionIndex
                    ? 'linear-gradient(90deg, #d4af37, #f4d03f)'
                    : i === questionIndex
                    ? 'rgba(212,175,55,0.4)'
                    : 'rgba(255,255,255,0.08)',
              }}
            />
          ))}
        </div>
      </div>

      {/* 提示语 */}
      <p className="text-[#c8b6ff]/45 text-xs tracking-wide mb-5 italic">
        {t('Choose the answer closest to your instinct — don\'t overthink ✦', '选择最接近你本能反应的答案——不要想太多 ✦')}
      </p>

      {/* 问题编号 + 文本 */}
      <div className="mb-8">
        <div className="flex items-start gap-4">
          <span
            className="text-4xl font-serif leading-none mt-1 flex-shrink-0"
            style={{ color: 'rgba(212,175,55,0.35)' }}
          >
            {String(questionIndex + 1).padStart(2, '0')}
          </span>
          <h2 className="text-xl lg:text-2xl font-serif text-white leading-relaxed">
            {question.question}
          </h2>
        </div>
      </div>

      {/* 选项 */}
      <div className="space-y-3">
        {question.options.map((option, idx) => {
          const isSelected = selected === idx;
          const isOther = selected !== null && !isSelected;
          const color = OPTION_COLORS[idx];
          return (
            <button
              key={idx}
              onClick={() => handleSelect(idx, option)}
              disabled={selected !== null}
              className="group relative w-full text-left rounded-2xl transition-all duration-350 cursor-pointer overflow-hidden"
              style={{
                border: `1px solid ${isSelected ? color.border : isOther ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.1)'}`,
                background: isSelected ? color.bg : isOther ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                opacity: isOther ? 0.4 : 1,
                transform: isSelected ? 'scale(1.01)' : isOther ? 'scale(0.99)' : 'scale(1)',
              }}
            >
              {/* 选中时左侧色条 */}
              {isSelected && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
                  style={{ background: color.border }}
                />
              )}

              <div className="flex items-center px-5 py-4 gap-4">
                {/* 标签字母 */}
                <div
                  className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full border text-sm font-bold transition-all duration-300"
                  style={
                    isSelected
                      ? { background: color.border, borderColor: color.border, color: '#0d0221' }
                      : {
                          borderColor: `${color.border}50`,
                          color: color.label,
                          background: `${color.border}10`,
                        }
                  }
                >
                  {isSelected ? <i className="ri-check-line text-sm"></i> : OPTION_LABELS[idx]}
                </div>

                {/* 文字 */}
                <span
                  className="flex-1 text-sm lg:text-base leading-relaxed transition-colors duration-300"
                  style={{ color: isSelected ? '#ffffff' : 'rgba(200,182,255,0.85)' }}
                >
                  {option.text}
                </span>

                {/* emoji */}
                <span
                  className="text-xl flex-shrink-0 transition-all duration-300"
                  style={{ opacity: isSelected ? 1 : 0.5 }}
                >
                  {option.emoji}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* 返回 */}
      {questionIndex > 0 && (
        <div className="mt-7 flex justify-start">
          <button
            onClick={onBack}
            disabled={selected !== null}
            className="flex items-center space-x-2 text-[#c8b6ff]/40 hover:text-[#c8b6ff] text-sm transition-colors cursor-pointer disabled:opacity-20"
          >
            <i className="ri-arrow-left-line text-base"></i>
            <span>{t('Previous', '上一题')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
